
import { UserProfile, getUserProfileAsync } from "@chr/online-shared-microsite-core";
import { Box, Card, CardContent, Grid, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useLayoutEffect, useState } from "react";
import { ErrorMessage } from "../errors/ErrorMessage";
import { toLocaleDateTime, useTranslation } from "@chr/online-shared-ui-localizations";
import { CallToActionButtons } from "../button/CallToActionButton";
import "./Homepage.scss";
import TrackingNumberWidget from "../widgets/TrackingNumberWidget";
import { ConfigureDashboard } from "../button/ConfigureDashboard";
import MaintenanceBanners from "../Banner/MaintenanceBanners";
import heroLowRes from '../../assets/img/hero-card-low-res.png';
import heroFull from '../../assets/img/hero-card.png';
import { CircularProgress } from '@chr/eds-react';

export const HERO_IMAGES = {
  placeholder: heroLowRes,
  full: heroFull
} as const;

export const HomepageExploration = () => {
  const [loadingUser, setLoadingUser] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userError, setUserError] = useState<Error | null>(null);
  const [lowResLoaded, setLowResLoaded] = useState(false);
  const [highResLoaded, setHighResLoaded] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [lastDevicePixelRatio, setLastDevicePixelRatio] = useState<number>(window.devicePixelRatio);
  const [shouldApplyCover, setShouldApplyCover] = useState(false);
  const translate = useTranslation();
  const theme = useTheme();
  const extraWide = useMediaQuery(theme.breakpoints.up(2500));

  useEffect(() => {
    // Function to load an image and return a promise
    const loadImage = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => {
          reject(new Error(`Failed to load image: ${src}`));
        };
        img.src = src;
      });
    };  
  
    getUserProfileAsync()
      .then((profile) => {
        setUserProfile(profile);
      })
      .catch(() => {
        setUserError(new Error('Failed to retrieve user'));
      })
      .finally(() => {
        setLoadingUser(false);
      });

    // Load low-res image first
    loadImage(HERO_IMAGES.placeholder)
      .then(() => {
        setLowResLoaded(true);
        return loadImage(HERO_IMAGES.full);
      })
      .then(() => {
        setHighResLoaded(true);
      })
      .catch((error) => {
        setImageError(error.message);
      });
  }, []);

  useLayoutEffect(() => {
    const checkZoomLevel = () => {
      const currentDevicePixelRatio = window.devicePixelRatio;
      if (currentDevicePixelRatio !== lastDevicePixelRatio) {
        const zoomPercent = currentDevicePixelRatio * 100;
        if (zoomPercent <= 70) {
          setShouldApplyCover(true);
        } else if (extraWide) {
          setShouldApplyCover(true);
        } else {
          setShouldApplyCover(false);
        }
      setLastDevicePixelRatio(currentDevicePixelRatio);
      }
    };
    window.addEventListener('resize', checkZoomLevel);
    return () => {
      window.removeEventListener('resize', checkZoomLevel);
    };
  }, []);

  const renderUser = () => {
    if (loadingUser) {
      return (
        <span>
          <CircularProgress size="sm" />
          Loading User
        </span>
      );
    }
    if (userError) {
      return <ErrorMessage error={userError} />;
    }
    if (userProfile) {
      return (
        <div className="username-buttons-wrapper">
          <div data-testid="username" className="username fs-mask">
            <span className="greeting">
              {`${getGreeting()}, ${userProfile.firstName}`}
            </span>
            <span className="getStarted">
              {translate('Here are a few ways to get started.')}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderButtons = () => {
    return (
      <div className="ctaButtons">
        <CallToActionButtons />
      </div>
    );
  }

  const getGreeting = () => {
    const date = new Date();
    const currentLocalHour = date.getHours();
    if (currentLocalHour >= 0 && currentLocalHour < 12) {
      return translate('Good morning');
    }
    else if (currentLocalHour >= 12 && currentLocalHour < 18) {
      return translate('Good afternoon');
    }
    else {
      return translate('Good evening');
    }
  };

  const getDate = () => {
    const dateFormatOptions: Intl.DateTimeFormatOptions = { 
      timeZone: 'UTC', 
      weekday: 'long', 
      month: 'long', 
      day: '2-digit' 
    };
    return (
      <Box className="date-header">
        <span>
          {toLocaleDateTime(new Date(Date.now()), dateFormatOptions)}
        </span>
      </Box>
    );
  };

  let imageUrl;

  if (highResLoaded) {
    imageUrl = HERO_IMAGES.full;
  } else {
    imageUrl = HERO_IMAGES.placeholder;
  }
  
  const currentBackgroundImage = !lowResLoaded ? 'none' : `url(${imageUrl})`;

  const heroClassName = `hero-module ${lowResLoaded ? 'hero-module--loaded' : ''}`;
  let backgroundSize = 'auto auto';

  if (extraWide) {
    backgroundSize = 'cover';
  } else {
    backgroundSize = shouldApplyCover ? 'cover' : 'auto auto';
  }

  return (
    <Box id="homepage-exploration-grid">
      <Grid container justifyContent="center">
        <Grid item xs={12}>
          <MaintenanceBanners />
        </Grid>
        <Grid item xs={12}>
          <ConfigureDashboard />
        </Grid>
        <Grid item xs={12} md={8}>
          {imageError ? (
            <Card className="hero-module">
              <CardContent>
                <ErrorMessage error={new Error(imageError)} />
              </CardContent>
            </Card>
          ) : (
            <Card 
              data-testid="hero-module"
              className={heroClassName}
              style={{
                backgroundImage: currentBackgroundImage,
                backgroundSize: backgroundSize,
                visibility: lowResLoaded ? 'visible' : 'hidden',
              }}
            >
              <CardContent sx={{padding: 0}}>
                {getDate()}
                {renderUser()}
                {renderButtons()}
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} md={4} className="tracking-widget-wrapper">
          <TrackingNumberWidget />
        </Grid>
      </Grid>
    </Box>
  );
}

export default HomepageExploration;